import { useEffect, useState } from "react";
import MetricCard from "./MetricCard";
import Suggestions from "../../ai/Suggestions";
import { toast } from "react-toastify";
import "../../common/tooltip.css";
import { extractDomain, getPerformanceCategory, normalizeUrl } from "../../../utils/helper";
import AllIssueDetails from "../AllIssueDetails";
import SocialPreview from "../../social/SocialPreview";
import { Circle, Square, Triangle } from "lucide-react";
import { Score } from "../../ui/Score";
import { AgenticBrowsingPassScore } from "../agentic/AgenticBrowsingPassScore";
import { Card, CardContent } from "../../ui/Card";
import { SegmentedControl } from "../../ui/DashboardLayout";
import { DEFAULT_AI_MODEL } from "../../../constants/aiModels";

const PerformanceMetrics = ({
  metrics,
  allMetrics,
  knownIssues,
  onClearResults,
  savedAt,
  aiModel,
  setAiModel,
}) => {
  const suggestionsDisabled =
    metrics.isSuggestionsDisabledByAdmin || !metrics.enableSuggestions;
  const [activeTab, setActiveTab] = useState(
    suggestionsDisabled ? "issues" : "suggestions",
  );

  const formatScore = (score) => {
    if (score === null || score === undefined) return 0;
    return Math.round(score);
  };

  const categorizeMetrics = (metricKey) => {
    switch (metricKey) {
      case "performanceScore":
        return {
          title: "Performance",
          desc: "Overall Performance Score",
          display: formatScore(metrics.performanceScore),
          category: getPerformanceCategory(metrics.performanceScore),
        };
      case "accessibilityScore":
        return {
          title: "Accessibility",
          desc: "Accessibility Score",
          display: `${formatScore(metrics.accessibilityScore)} / 100`,
          category: getPerformanceCategory(metrics.accessibilityScore),
        };
      case "bestPracticesScore":
        return {
          title: "Best Practices",
          desc: "Best Practices Score",
          display: `${formatScore(metrics.bestPracticesScore)} / 100`,
          category: getPerformanceCategory(metrics.bestPracticesScore),
        };
      case "seoScore":
        return {
          title: "SEO",
          desc: "SEO Score",
          display: `${formatScore(metrics.seoScore)} / 100`,
          category: getPerformanceCategory(metrics.seoScore),
        };
      case "fcp":
        return {
          title: "FCP",
          desc: "First Contentful Paint",
          display: metrics.fcp?.display || "N/A",
          category: getPerformanceCategory(metrics.fcp?.score * 100),
        };
      case "lcp":
        return {
          title: "LCP",
          desc: "Largest Contentful Paint",
          display: metrics.lcp?.display || "N/A",
          category: getPerformanceCategory(metrics.lcp?.score * 100),
        };
      case "cls":
        return {
          title: "CLS",
          desc: "Cumulative Layout Shift",
          display: metrics.cls?.display || "N/A",
          category: getPerformanceCategory(metrics.cls?.score * 100),
        };
      case "tbt":
        return {
          title: "TBT",
          desc: "Total Blocking Time",
          display: metrics.tbt?.display || "N/A",
          category: getPerformanceCategory(metrics.tbt?.score * 100),
        };
      case "speedIndex":
        return {
          title: "Speed Index",
          desc: "Speed Index Measurement",
          display: metrics.speedIndex?.display || "N/A",
          category: getPerformanceCategory(metrics.speedIndex?.score * 100),
        };

      default:
        return {
          title: metricKey,
          desc: "Unknown Metric",
          display: "Unknown",
          category: "unknown",
        };
    }
  };

  const metricDetails = ["fcp", "lcp", "cls", "tbt", "speedIndex"]
    .map((key) => {
      if (!metrics[key]) return null;
      const { title, desc, display, category } = categorizeMetrics(key);
      return { key, title, desc, display, category };
    })
    .filter(Boolean);

  const getMetricIcon = (category) => {
    switch (category) {
      case "Good":
        return <Circle size={10} className="text-green-500 fill-current" />;
      case "Needs Improvement":
        return <Square size={10} className="text-orange-500 fill-current" />;
      case "Poor":
        return <Triangle size={10} className="text-red-500 fill-current" />;
      default:
        return <div className="w-[10px]" />; // placeholder for spacing
    }
  };

  const getMetricColor = (category) => {
    switch (category) {
      case "Good":
        return "text-green-600";
      case "Needs Improvement":
        return "text-orange-600";
      case "Poor":
        return "text-red-600";
      default:
        return "text-foreground";
    }
  };

  useEffect(() => {
    if (metrics?.suggestions) {
      if (metrics.suggestions.startsWith("Failed")) {
        toast.error(
          "Failed to generate suggestions, but metrics were loaded successfully",
        );
      } else if (metrics.suggestions === "No suggestions available") {
        toast.info("No suggestions available for these metrics");
      }
    }
  }, [metrics]);

  // Countdown logic for clear button
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    if (!savedAt) return;
    const update = () => {
      const now = Date.now();
      const expires = savedAt + 10 * 60 * 1000;
      setRemaining(Math.max(0, Math.floor((expires - now) / 1000)));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [savedAt]);
  useEffect(() => {
    if (remaining === 0) {
      onClearResults();
    }
  }, [remaining, onClearResults]);
  function formatMMSS(secs) {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }

  if (!metrics) {
    return null;
  }

  const deviceLabel =
    metrics.device?.charAt(0)?.toUpperCase() +
      metrics.device?.slice(1)?.toLowerCase() || "Unknown";
  const domainLabel = extractDomain(metrics.url) || metrics.url;
  const siteUrl = normalizeUrl(metrics.url);
  const issueCount = knownIssues
    ? Object.values(knownIssues).filter((i) => i?.score != null && i.score < 1)
        .length
    : 0;

  const formattedTestTime = (() => {
    const d = new Date(metrics.timestamp);
    const day = d.getDate();
    const month = d.toLocaleString("default", { month: "short" });
    const hour = d.getHours().toString().padStart(2, "0");
    const min = d.getMinutes().toString().padStart(2, "0");
    return `${day} ${month}, ${hour}:${min}`;
  })();

  const clearCountdownLabel =
    remaining !== null ? `Clears in ${formatMMSS(remaining)}` : "Clear results";

  return (
    <div className="mx-auto max-w-3xl">
      <header className="text-center mb-8">
        <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
          {deviceLabel}
          <span className="text-muted-foreground font-normal mx-2">·</span>
          {siteUrl ? (
            <a
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground font-medium hover:underline underline-offset-2"
            >
              {domainLabel}
            </a>
          ) : (
            <span className="text-muted-foreground">{domainLabel}</span>
          )}
        </h2>
        <div className="mt-2 inline-flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground">
          <span>Tested {formattedTestTime}</span>
          <span className="text-border" aria-hidden>
            ·
          </span>
          <button
            type="button"
            onClick={onClearResults}
            className="group relative inline-block w-[6.875rem] shrink-0 text-left tabular-nums text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className="block transition-opacity group-hover:opacity-0">
              {clearCountdownLabel}
            </span>
            <span className="absolute inset-0 flex items-center opacity-0 transition-opacity group-hover:opacity-100">
              Clear now
            </span>
          </button>
        </div>
      </header>

      <div className="mb-10 flex flex-col items-center">
        <div
          className={`grid gap-4 sm:gap-6 ${
            metrics.agenticBrowsing?.totalCount
              ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
              : "grid-cols-2 sm:grid-cols-4"
          }`}
        >
          <Score
            value={metrics.performanceScore}
            label="Performance"
          />
          <Score
            value={metrics.accessibilityScore}
            label="Accessibility"
          />
          <Score
            value={metrics.bestPracticesScore}
            label="Best Practices"
          />
          <Score value={metrics.seoScore} label="SEO" />
          {metrics.agenticBrowsing?.totalCount > 0 && (
            <AgenticBrowsingPassScore
              passCount={metrics.agenticBrowsing.passCount}
              totalCount={metrics.agenticBrowsing.totalCount}
            />
          )}
        </div>
      </div>

      <div className="flex justify-center items-center">
        <Card className="relative w-full overflow-hidden">
          {metrics.finalScreenshot && (
            <div className="px-4 pt-5 pb-2 bg-muted/30">
              <img
                src={metrics.finalScreenshot}
                alt="Page preview"
                className="mx-auto max-w-full max-h-[16em] object-contain rounded-lg border border-border"
              />
            </div>
          )}

          <div className="px-4 py-4 border-t border-border">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-3">
              Core Web Vitals
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
              {metricDetails.map(({ key, desc, display, category }) => (
                <div key={key} className="py-3 border-b md:border-none">
                  {/* Mobile View: Single Line */}
                  <div className="flex justify-between items-center md:hidden">
                    <div className="flex items-center space-x-2">
                      {getMetricIcon(category)}
                      <span className="text-foreground text-sm">{desc}</span>
                    </div>
                    <div
                      className={`font-semibold text-lg ${getMetricColor(
                        category,
                      )}`}
                    >
                      {display}
                    </div>
                  </div>

                  {/* Desktop View: Two Lines */}
                  <div className="hidden md:block">
                    <div className="flex items-center space-x-2">
                      {getMetricIcon(category)}
                      <span className="text-foreground text-sm">{desc}</span>
                    </div>
                    <div
                      className={`font-semibold text-xl pl-[18px] ${getMetricColor(
                        category,
                      )}`}
                    >
                      {display}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <MetricCard metrics={metrics} allMetrics={allMetrics} />
        </Card>
      </div>

      <div className="flex justify-center items-center mt-8">
        <Card className="relative w-full overflow-visible">
          <CardContent className="p-4 sm:p-5">
            <div className="flex justify-center">
              <SegmentedControl
                options={[
                  { value: "suggestions", label: "AI Summary" },
                  {
                    value: "issues",
                    label:
                      issueCount > 0 ? `Issues (${issueCount})` : "Issues",
                  },
                  { value: "social", label: "Social" },
                ]}
                value={activeTab}
                onChange={setActiveTab}
              />
            </div>

            <div className="mt-5">
              {activeTab === "suggestions" && (
                <>
                  {metrics.isSuggestionsDisabledByAdmin ? (
                    <p className="text-muted-foreground text-center mb-8">
                      AI Suggestions are disabled by Admin.
                    </p>
                  ) : metrics.enableSuggestions ? (
                    metrics.suggestions &&
                    !metrics.suggestions.startsWith("Failed") ? (
                      <Suggestions
                        metrics={metrics}
                        currentAiModel={aiModel}
                        knownIssues={knownIssues}
                        setAiModel={setAiModel}
                        onOpenSocialTab={() => setActiveTab("social")}
                      />
                    ) : (
                      <p className="text-yellow-600 text-center mb-8">
                        AI Suggestions are not available for this analysis.
                      </p>
                    )
                  ) : (
                    <p className="text-muted-foreground text-center mb-8">
                      AI Suggestions are disabled.
                    </p>
                  )}
                </>
              )}

              {activeTab === "issues" && (
                <AllIssueDetails
                  knownIssues={knownIssues}
                  screenshot={metrics.fullPageScreenshot}
                  url={metrics.url}
                  strategy={metrics.device}
                  agenticBrowsing={metrics.agenticBrowsing}
                />
              )}

              {activeTab === "social" && (
                <SocialPreview
                  socialMeta={metrics.socialMeta}
                  url={metrics.url}
                  aiModel={aiModel || metrics.aiModel || DEFAULT_AI_MODEL}
                  setAiModel={setAiModel}
                  aiDisabled={!!metrics.isSuggestionsDisabledByAdmin}
                  knownIssues={knownIssues}
                  seoScore={metrics.seoScore}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PerformanceMetrics;
